import type { TranslationMap } from "../lib/types.ts";

export const en: TranslationMap = {
  common: {
    health: "Health",
    ok: "OK",
    online: "Online",
    offline: "Offline",
    connect: "Connect",
    refresh: "Refresh",
    enabled: "Enabled",
    disabled: "Disabled",
    na: "n/a",
    version: "Version",
    docs: "Docs",
    theme: "Theme",
    resources: "Resources",
    search: "Search",
    cancel: "Cancel",
    delete: "Delete",
    confirm: "Confirm",
    save: "Save",
    create: "Create",
    close: "Close",
    edit: "Edit",
    reset: "Reset",
    searchPlaceholder: "Search…",
    noResults: "No results",
    select: "Select…",
    loading: "Loading…",
    saving: "Saving…",
    remove: "Remove",
    copy: "Copy",
    actions: "Actions",
    back: "Back",
    next: "Next",
    optional: "optional",
    missing: "Missing",
    add: "Add",
    selectAll: "Select All",
    clear: "Clear",
    all: "All",
    to: "to",
    noContent: "No content available",
    refreshing: "Refreshing…",
    sort: "Sort",
    yes: "Yes",
    no: "No",
    saved: "Changes saved",
    saveFailed: "Save failed",
  },
  nav: {
    chat: "Chat",
    control: "Control",
    agent: "Agent",
    settings: "Settings",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    resize: "Resize sidebar",
  },
  tabs: {
    agents: "Agents",
    agentsCanvas: "Agents Canvas",
    overview: "Overview",
    channels: "Channels",
    instances: "Instances",
    sessions: "Sessions",
    usage: "Usage",
    cron: "Cron Jobs",
    skills: "Skills",
    nodes: "Nodes",
    chat: "Chat",
    config: "Config",
    communications: "Communications",
    appearance: "Appearance",
    automation: "Automation",
    infrastructure: "Infrastructure",
    aiAgents: "AI & Agents",
    debug: "Debug",
    logs: "Logs",
  },
  subtitles: {
    agents: "Workspaces, tools, identities.",
    agentsCanvas: "Temporary preview of the legacy Nodes/Charts canvas.",
    overview: "Status, entry points, health.",
    channels: "Channels and settings.",
    instances: "Connected clients and nodes.",
    sessions: "Active sessions and defaults.",
    usage: "API usage and costs.",
    cron: "Wakeups and recurring runs.",
    skills: "Skills and API keys.",
    nodes: "Paired devices and commands.",
    chat: "Gateway chat for quick interventions.",
    config: "Edit puiuxOne.json.",
    communications: "Channels, messages, and audio settings.",
    appearance: "Theme, UI, and setup wizard settings.",
    automation: "Commands, hooks, cron, and plugins.",
    infrastructure: "Gateway, web, browser, and media settings.",
    aiAgents: "Agents, models, skills, tools, memory, session.",
    debug: "Snapshots, events, RPC.",
    logs: "Live gateway logs.",
  },
  overview: {
    access: {
      title: "Gateway Access",
      subtitle: "Where the dashboard connects and how it authenticates.",
      wsUrl: "WebSocket URL",
      token: "Gateway Token",
      password: "Password (not stored)",
      sessionKey: "Default Session Key",
      language: "Language",
      connectHint: "Click Connect to apply connection changes.",
      trustedProxy: "Authenticated via trusted proxy.",
    },
    snapshot: {
      title: "Snapshot",
      subtitle: "Latest gateway handshake information.",
      status: "Status",
      uptime: "Uptime",
      tickInterval: "Tick Interval",
      lastChannelsRefresh: "Last Channels Refresh",
      channelsHint: "Use Channels to link WhatsApp, Telegram, Discord, Signal, or iMessage.",
    },
    stats: {
      instances: "Instances",
      instancesHint: "Presence beacons in the last 5 minutes.",
      sessions: "Sessions",
      sessionsHint: "Recent session keys tracked by the gateway.",
      cron: "Cron",
      cronNext: "Next wake {time}",
    },
    notes: {
      title: "Notes",
      subtitle: "Quick reminders for remote control setups.",
      tailscaleTitle: "Tailscale serve",
      tailscaleText: "Prefer serve mode to keep the gateway on loopback with tailnet auth.",
      sessionTitle: "Session hygiene",
      sessionText: "Use /new or sessions.patch to reset context.",
      cronTitle: "Cron reminders",
      cronText: "Use isolated sessions for recurring runs.",
    },
    auth: {
      required: "This gateway requires auth. Add a token or password, then click Connect.",
      failed:
        "Auth failed. Re-copy a tokenized URL with {command}, or update the token, then click Connect.",
    },
    pairing: {
      hint: "This device needs pairing approval from the gateway host.",
      mobileHint:
        "On mobile? Copy the full URL (including #token=...) from puiux-one dashboard --no-open on your desktop.",
    },
    insecure: {
      hint: "This page is HTTP, so the browser blocks device identity. Use HTTPS (Tailscale Serve) or open {url} on the gateway host.",
      stayHttp: "If you must stay on HTTP, set {config} (token-only).",
    },
    connection: {
      title: "How to connect",
      step1: "Start the gateway on your host machine:",
      step2: "Get a tokenized dashboard URL:",
      step3: "Paste the WebSocket URL and token above, or open the tokenized URL directly.",
      step4: "Or generate a reusable token:",
      docsHint: "For remote access, Tailscale Serve is recommended. ",
      docsLink: "Read the docs →",
    },
    cards: {
      cost: "Cost",
      skills: "Skills",
      recentSessions: "Recent Sessions",
    },
    attention: {
      title: "Attention",
    },
    eventLog: {
      title: "Event Log",
    },
    logTail: {
      title: "Gateway Logs",
    },
    quickActions: {
      newSession: "New Session",
      automation: "Automation",
      refreshAll: "Refresh All",
      terminal: "Terminal",
    },
    palette: {
      placeholder: "Type a command…",
      noResults: "No results",
    },
  },
  usage: {
    page: {
      subtitle: "See where tokens go, when sessions spike, and what drives cost.",
    },
    common: {
      emptyValue: "—",
      unknown: "unknown",
    },
    loading: {
      title: "Usage Overview",
      badge: "Loading",
    },
    metrics: {
      tokens: "Tokens",
      cost: "Cost",
      session: "session",
      sessions: "sessions",
    },
    presets: {
      today: "Today",
      last7d: "7d",
      last30d: "30d",
    },
    filters: {
      title: "Filters",
      to: "to",
      startDate: "Start date",
      endDate: "End date",
      timeZone: "Time zone",
      timeZoneLocal: "Local",
      timeZoneUtc: "UTC",
      pin: "Pin",
      pinned: "Pinned",
      unpin: "Unpin filters",
      selectAll: "Select All",
      clear: "Clear",
      clearAll: "Clear All",
      remove: "Remove filter",
      all: "All",
      days: "Days",
      hours: "Hours",
      session: "Session",
      agent: "Agent",
      channel: "Channel",
      provider: "Provider",
      model: "Model",
      tool: "Tool",
      daysCount: "{count} days",
      hoursCount: "{count} hours",
      sessionsCount: "{count} sessions",
    },
    query: {
      placeholder:
        "Filter sessions (e.g. key:agent:main:cron* model:gpt-4o has:errors minTokens:2000)",
      apply: "Filter (client-side)",
      matching: "{shown} of {total} sessions match",
      inRange: "{total} sessions in range",
      tip: "Tip: use filters or click bars to refine days.",
    },
    export: {
      label: "Export",
      sessionsCsv: "Sessions CSV",
      dailyCsv: "Daily CSV",
      json: "JSON",
    },
    empty: {
      title: "Start with a date range",
      subtitle:
        "Load usage data to compare costs, inspect sessions, and drill into timelines without leaving the dashboard.",
      hint: "Select a date range and click Refresh to load usage.",
      noData: "No data",
      featureOverview: "Overview cards",
      featureSessions: "Session ranking",
      featureTimeline: "Timeline drilldown",
    },
    daily: {
      title: "Daily Usage",
      total: "Total",
      byType: "By Type",
      tokensTitle: "Daily Token Usage",
      costTitle: "Daily Cost",
    },
    breakdown: {
      output: "Output",
      input: "Input",
      cacheWrite: "Cache Write",
      cacheRead: "Cache Read",
      total: "Total",
      tokensByType: "Tokens by Type",
      costByType: "Cost by Type",
    },
    overview: {
      title: "Usage Overview",
      messages: "Messages",
      messagesHint: "Total user and assistant messages in range.",
      messagesAbbrev: "msgs",
      user: "user",
      assistant: "assistant",
      toolCalls: "Tool Calls",
      toolCallsHint: "Total tool call count across sessions.",
      toolsUsed: "tools used",
      errors: "Errors",
      errorsHint: "Total message and tool errors in range.",
      toolResults: "tool results",
      avgTokens: "Avg Tokens / Msg",
      avgTokensHint: "Average tokens per message in this range.",
      avgCost: "Avg Cost / Msg",
      avgCostHint: "Average cost per message when providers report costs.",
      avgCostHintMissing:
        "Average cost per message when providers report costs. Cost data is missing for some or all sessions in this range.",
      acrossMessages: "Across {count} messages",
      sessions: "Sessions",
      sessionsHint: "Distinct sessions in the range.",
      sessionsInRange: "of {count} in range",
      throughput: "Throughput",
      throughputHint: "Throughput shows tokens per minute over active time. Higher is better.",
      tokensPerMinute: "tok/min",
      perMinute: "/ min",
      errorRate: "Error Rate",
      errorHint: "Error rate = errors / total messages. Lower is better.",
      avgSession: "avg session",
      cacheHitRate: "Cache Hit Rate",
      cacheHint: "Cache hit rate = cache read / (input + cache read). Higher is better.",
      cached: "cached",
      prompt: "prompt",
      calls: "calls",
      topModels: "Top Models",
      topProviders: "Top Providers",
      topTools: "Top Tools",
      topAgents: "Top Agents",
      topChannels: "Top Channels",
      peakErrorDays: "Peak Error Days",
      peakErrorHours: "Peak Error Hours",
      noModelData: "No model data",
      noProviderData: "No provider data",
      noToolCalls: "No tool calls",
      noAgentData: "No agent data",
      noChannelData: "No channel data",
      noErrorData: "No error data",
    },
    sessions: {
      title: "Sessions",
      shown: "{count} shown",
      total: "{count} total",
      avg: "avg",
      all: "All",
      recent: "Recently viewed",
      recentShort: "Recent",
      sort: "Sort",
      ascending: "Ascending",
      descending: "Descending",
      clearSelection: "Clear Selection",
      noRecent: "No recent sessions",
      noneInRange: "No sessions in range",
      more: "+{count} more",
      selected: "Selected ({count})",
      copy: "Copy",
      copyName: "Copy session name",
      limitReached: "Showing first 1,000 sessions. Narrow date range for complete results.",
    },
    details: {
      noUsageData: "No usage data for this session.",
      duration: "Duration",
      modelMix: "Model Mix",
      filtered: "(filtered)",
      close: "Close session details",
      noTimeline: "No timeline data",
      noDataInRange: "No data in range",
      usageOverTime: "Usage Over Time",
      reset: "Reset",
      perTurn: "Per Turn",
      cumulative: "Cumulative",
      turnRange: "Turns {start}–{end} of {total}",
      assistantOutputTokens: "Assistant output tokens",
      userToolInputTokens: "User + tool input tokens",
      tokensWrittenToCache: "Tokens written to cache",
      tokensReadFromCache: "Tokens read from cache",
      noContextData: "No context data",
      systemPromptBreakdown: "System Prompt Breakdown",
      collapse: "Collapse",
      collapseAll: "Collapse All",
      expandAll: "Expand All",
      baseContextPerMessage: "Base context per message",
      system: "System",
      systemShort: "Sys",
      skills: "Skills",
      tools: "Tools",
      files: "Files",
      ofInput: "of input",
      of: "of",
      timelineFiltered: "timeline filtered",
      conversation: "Conversation",
      noMessages: "No messages",
      tool: "Tool",
      toolResult: "Tool result",
      hasTools: "Has tools",
      searchConversation: "Search conversation",
      you: "You",
      noMessagesMatch: "No messages match the filters.",
    },
    mosaic: {
      title: "Activity by Time",
      subtitleEmpty: "Estimates require session timestamps.",
      subtitle: "Estimated from session spans (first/last activity). Time zone: {zone}.",
      noTimelineData: "No timeline data yet.",
      dayOfWeek: "Day of Week",
      midnight: "Midnight",
      fourAm: "4am",
      eightAm: "8am",
      noon: "Noon",
      fourPm: "4pm",
      eightPm: "8pm",
      legend: "Low → High token density",
      sun: "Sun",
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
    },
  },
  login: {
    subtitle: "Gateway Dashboard",
    passwordPlaceholder: "optional",
  },
  chat: {
    disconnected: "Disconnected from gateway.",
    refreshTitle: "Refresh chat data",
    thinkingToggle: "Toggle assistant thinking/working output",
    toolCallsToggle: "Toggle tool calls and tool results",
    focusToggle: "Toggle focus mode (hide sidebar + page header)",
    hideCronSessions: "Hide cron sessions",
    showCronSessions: "Show cron sessions",
    showCronSessionsHidden: "Show cron sessions ({count} hidden)",
    onboardingDisabled: "Disabled during setup",
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Simplified Chinese)",
    zhTW: "繁體中文 (Traditional Chinese)",
    ptBR: "Português (Brazilian Portuguese)",
    de: "Deutsch (German)",
    es: "Español (Spanish)",
  },
  cron: {
    summary: {
      enabled: "Enabled",
      yes: "Yes",
      no: "No",
      jobs: "Jobs",
      nextWake: "Next wake",
      refreshing: "Refreshing...",
      refresh: "Refresh",
    },
    jobs: {
      title: "Jobs",
      subtitle: "All scheduled jobs stored in the gateway.",
      shownOf: "{shown} shown of {total}",
      searchJobs: "Search jobs",
      searchPlaceholder: "Name, description, or agent",
      enabled: "Enabled",
      schedule: "Schedule",
      lastRun: "Last run",
      all: "All",
      sort: "Sort",
      nextRun: "Next run",
      recentlyUpdated: "Recently updated",
      name: "Name",
      direction: "Direction",
      ascending: "Ascending",
      descending: "Descending",
      reset: "Reset",
      noMatching: "No matching jobs.",
      loading: "Loading...",
      loadMore: "Load more jobs",
    },
    runs: {
      title: "Run history",
      subtitleAll: "Latest runs across all jobs.",
      subtitleJob: "Latest runs for {title}.",
      scope: "Scope",
      allJobs: "All jobs",
      selectedJob: "Selected job",
      searchRuns: "Search runs",
      searchPlaceholder: "Summary, error, or job",
      newestFirst: "Newest first",
      oldestFirst: "Oldest first",
      status: "Status",
      delivery: "Delivery",
      clear: "Clear",
      allStatuses: "All statuses",
      allDelivery: "All delivery",
      selectJobHint: "Select a job to inspect run history.",
      noMatching: "No matching runs.",
      loadMore: "Load more runs",
      runStatusOk: "OK",
      runStatusError: "Error",
      runStatusSkipped: "Skipped",
      runStatusUnknown: "Unknown",
      deliveryDelivered: "Delivered",
      deliveryNotDelivered: "Not delivered",
      deliveryUnknown: "Unknown",
      deliveryNotRequested: "Not requested",
    },
    form: {
      editJob: "Edit Job",
      newJob: "New Job",
      updateSubtitle: "Update the selected scheduled job.",
      createSubtitle: "Create a scheduled wakeup or agent run.",
      required: "Required",
      requiredSr: "required",
      basics: "Basics",
      basicsSub: "Name it, choose the assistant, and set enabled state.",
      fieldName: "Name",
      description: "Description",
      agentId: "Agent ID",
      namePlaceholder: "Morning brief",
      descriptionPlaceholder: "Optional context for this job",
      agentPlaceholder: "main or ops",
      agentHelp: "Start typing to pick a known agent, or enter a custom one.",
      schedule: "Schedule",
      scheduleSub: "Control when this job runs.",
      every: "Every",
      at: "At",
      cronOption: "Cron",
      runAt: "Run at",
      unit: "Unit",
      minutes: "Minutes",
      hours: "Hours",
      days: "Days",
      expression: "Expression",
      expressionPlaceholder: "0 7 * * *",
      everyAmountPlaceholder: "30",
      timezoneOptional: "Timezone (optional)",
      timezonePlaceholder: "America/Los_Angeles",
      timezoneHelp: "Pick a common timezone or enter any valid IANA timezone.",
      jitterHelp: "Need jitter? Use Advanced → Stagger window / Stagger unit.",
      execution: "Execution",
      executionSub: "Choose when to wake, and what this job should do.",
      session: "Session",
      main: "Main",
      isolated: "Isolated",
      sessionHelp: "Main posts a system event. Isolated runs a dedicated agent turn.",
      wakeMode: "Wake mode",
      now: "Now",
      nextHeartbeat: "Next heartbeat",
      wakeModeHelp: "Now triggers immediately. Next heartbeat waits for the next cycle.",
      payloadKind: "What should run?",
      systemEvent: "Post message to main timeline",
      agentTurn: "Run assistant task (isolated)",
      systemEventHelp:
        "Sends your text to the gateway main timeline (good for reminders/triggers).",
      agentTurnHelp: "Starts an assistant run in its own session using your prompt.",
      timeoutSeconds: "Timeout (seconds)",
      timeoutPlaceholder: "Optional, e.g. 90",
      timeoutHelp:
        "Optional. Leave blank to use the gateway default timeout behavior for this run.",
      mainTimelineMessage: "Main timeline message",
      assistantTaskPrompt: "Assistant task prompt",
      deliverySection: "Delivery",
      deliverySub: "Choose where run summaries are sent.",
      resultDelivery: "Result delivery",
      announceDefault: "Announce summary (default)",
      webhookPost: "Webhook POST",
      noneInternal: "None (internal)",
      deliveryHelp: "Announce posts a summary to chat. None keeps execution internal.",
      webhookUrl: "Webhook URL",
      channel: "Channel",
      webhookPlaceholder: "https://example.com/cron",
      channelHelp: "Choose which connected channel receives the summary.",
      webhookHelp: "Send run summaries to a webhook endpoint.",
      to: "To",
      toPlaceholder: "+1555... or chat id",
      toHelp: "Optional recipient override (chat id, phone, or user id).",
      advanced: "Advanced",
      advancedHelp:
        "Optional overrides for delivery guarantees, schedule jitter, and model controls.",
      deleteAfterRun: "Delete after run",
      deleteAfterRunHelp: "Best for one-shot reminders that should auto-clean up.",
      clearAgentOverride: "Clear agent override",
      clearAgentHelp: "Force this job to use the gateway default assistant.",
      exactTiming: "Exact timing (no stagger)",
      exactTimingHelp: "Run on exact cron boundaries with no spread.",
      staggerWindow: "Stagger window",
      staggerUnit: "Stagger unit",
      staggerPlaceholder: "30",
      seconds: "Seconds",
      model: "Model",
      modelPlaceholder: "openai/gpt-5.2",
      modelHelp: "Start typing to pick a known model, or enter a custom one.",
      thinking: "Thinking",
      thinkingPlaceholder: "low",
      thinkingHelp: "Use a suggested level or enter a provider-specific value.",
      bestEffortDelivery: "Best effort delivery",
      bestEffortHelp: "Do not fail the job if delivery itself fails.",
      cantAddYet: "Can't add job yet",
      fillRequired: "Fill the required fields below to enable submit.",
      fixFields: "Fix {count} field to continue.",
      fixFieldsPlural: "Fix {count} fields to continue.",
      saving: "Saving...",
      saveChanges: "Save changes",
      addJob: "Add job",
      cancel: "Cancel",
    },
    jobList: {
      allJobs: "all jobs",
      selectJob: "(select a job)",
      enabled: "enabled",
      disabled: "disabled",
      edit: "Edit",
      clone: "Clone",
      disable: "Disable",
      enable: "Enable",
      run: "Run",
      history: "History",
      remove: "Remove",
    },
    jobDetail: {
      system: "System",
      prompt: "Prompt",
      delivery: "Delivery",
      agent: "Agent",
    },
    jobState: {
      status: "Status",
      next: "Next",
      last: "Last",
    },
    runEntry: {
      noSummary: "No summary.",
      runAt: "Run at",
      openRunChat: "Open run chat",
      next: "Next {rel}",
      due: "Due {rel}",
    },
    errors: {
      nameRequired: "Name is required.",
      scheduleAtInvalid: "Enter a valid date/time.",
      everyAmountInvalid: "Interval must be greater than 0.",
      cronExprRequired: "Cron expression is required.",
      staggerAmountInvalid: "Stagger must be greater than 0.",
      systemTextRequired: "System text is required.",
      agentMessageRequired: "Agent message is required.",
      timeoutInvalid: "If set, timeout must be greater than 0 seconds.",
      webhookUrlRequired: "Webhook URL is required.",
      webhookUrlInvalid: "Webhook URL must start with http:// or https://.",
      invalidRunTime: "Invalid run time.",
      invalidIntervalAmount: "Invalid interval amount.",
      cronExprRequiredShort: "Cron expression required.",
      invalidStaggerAmount: "Invalid stagger amount.",
      systemEventTextRequired: "System event text required.",
      agentMessageRequiredShort: "Agent message required.",
      nameRequiredShort: "Name required.",
    },
  },
  agents: {
    panelOverview: "Overview",
    panelFiles: "Files",
    panelTools: "Tools",
    panelSkills: "Skills",
    panelChannels: "Channels",
    channelsEmpty: "No channels bound to this agent.",
    panelSchedule: "Schedule",
    organizations: "Organizations",
    orgCreate: "Create Organization",
    orgEdit: "Edit Organization",
    orgDelete: "Delete Organization",
    orgName: "Organization Name",
    orgIndustry: "Industry",
    orgDescription: "Description",
    orgColor: "Brand Color",
    orgTeams: "Teams",
    orgAgents: "Agents",
    orgNoOrgs: "No organizations yet. Create your first one.",
    teamAdd: "Add Team",
    teamName: "Team Name",
    teamRole: "Role",
    teamParent: "Parent Team",
    teamAgents: "Assigned Agents",
    wizardStep1: "Basic Info",
    wizardStep2: "Team Structure",
    wizardStep3: "Assign Agents",
    wizardStep4: "Review",
    wizardNext: "Next",
    wizardBack: "Back",
    wizardSave: "Save Organization",
    confirmDelete: "Are you sure you want to delete this organization?",
    assignAgent: "Assign Agent",
    unassignAgent: "Remove Agent",
    soulUpdated: "Agent context updated",
    orgEmoji: "Emoji",
    orgNameHint: "e.g. FitMe Health Clinic",
    orgDescHint: "Short description of the organization...",
    noTeamsHint: "Add your first team to build the hierarchy.",
    noTeamsGoBack: "No teams yet. Go back to add teams first.",
    unnamed: "Unnamed",
    teamAddChild: "Add sub-team",
    teamRemove: "Remove",
    addDepartment: "Add Department",
    rootTeamHint: "e.g. Executive Management",
    roleRoot: "Root",
    roleDepartment: "Department",
    roleDivision: "Division",
    roleUnit: "Unit",
    // Industries
    industryTech: "Technology",
    industryTelecom: "Telecom",
    industryEcommerce: "E-commerce",
    industryGaming: "Gaming",
    industryHealthcare: "Healthcare",
    industryPharma: "Pharmaceuticals",
    industryDental: "Dental",
    industryFinance: "Finance",
    industryInsurance: "Insurance",
    industryAccounting: "Accounting",
    industryEducation: "Education",
    industryTraining: "Training",
    industryLaw: "Law",
    industryRealestate: "Real Estate",
    industryConstruction: "Construction",
    industryRestaurant: "Restaurants & Cafés",
    industryHospitality: "Hospitality",
    industryTourism: "Tourism",
    industryRetail: "Retail",
    industryFashion: "Fashion",
    industryBeauty: "Beauty & Wellness",
    industryMedia: "Media & Publishing",
    industryAdvertising: "Advertising",
    industryDesign: "Design",
    industryManufacturing: "Manufacturing",
    industryAutomotive: "Automotive",
    industryEnergy: "Energy & Utilities",
    industryAgriculture: "Agriculture",
    industryConsulting: "Consulting",
    industryLogistics: "Logistics & Shipping",
    industryHr: "Human Resources",
    industryGovernment: "Government",
    industryNonprofit: "Non-profit",
    industryOther: "Other",
    // Industry groups
    groupTech: "Technology",
    groupHealth: "Healthcare",
    groupFinance: "Finance & Banking",
    groupEducation: "Education",
    groupLegal: "Legal",
    groupProperty: "Real Estate & Construction",
    groupFood: "Food & Hospitality",
    groupRetail: "Retail & Fashion",
    groupMedia: "Media & Creative",
    groupIndustrial: "Industry & Manufacturing",
    groupServices: "Professional Services",
    groupGovt: "Government & Non-profit",
    groupOther: "Other",
    searchIndustry: "Search industries...",
    // Emoji categories
    emojiBuildings: "Buildings",
    emojiBusiness: "Business",
    emojiPeople: "People",
    emojiTech: "Technology",
    emojiHealth: "Health",
    emojiNature: "Nature",
    emojiFood: "Food & Drink",
    emojiTransport: "Transport",
    emojiCreative: "Creative",
    emojiSymbols: "Symbols",
    // Misc
    teamsNeedNames: "Some teams need names",
    moveAgent: "Move Agent",
    moveAgentConfirm: "Move",
    moveFrom: "from",
    moveConfirm: "Move",
    searchAgents: "Search agents...",
    createNewAgent: "Create New Agent",
    createAndAssign: "Create & Assign",
    agentName: "Name",
    agentNameHint: "e.g. Sales Manager",
    noAgentsFound: "No agents found",
    createAgentFailed: "Failed to create agent. Please try again.",
    availableAgents: "Available",
    moveFromTeam: "Move from another team",
    ghostAgent: "Missing agent",
    ghostAgentWarning: "This agent does not exist in the system yet",
    ghostAgentsAutoCreate: "These agents will be auto-created on save:",
    // Agent delete
    deleteAgent: "Delete Agent",
    deleteAgentConfirm: "This will permanently remove the agent and all its files.",
    // New Agent form
    newAgent: "New Agent",
    agentNamePlaceholder: "Agent name",
    parentAutoDetect: "Parent: Auto-detect",
    parentStandalone: "Standalone (Main Agent)",
    createAgent: "Create Agent",
    creating: "Creating...",
    noAgentsToDisplay: "No agents to display",
    autoDetect: "Auto-detect",
    standalone: "Standalone (Main Agent)",
    parent: "Parent",
    searchAgentsPlaceholder: "Search agents…",
    clearSearch: "Clear search",
    createAgentAria: "Create agent",
    hideAgentsPanel: "Hide agents panel",
    showAgentsPanel: "Show agents panel",
    hideOrgsPanel: "Hide organizations panel",
    showOrgsPanel: "Show organizations panel",
    exitFullscreen: "Exit fullscreen",
    fullscreen: "Fullscreen",
    agentSections: "Agent sections",
    noSkillsMatch: "No skills match.",
    searchSkills: "Search skills…",
    // Canvas panels
    panelCoreFiles: "Core Files",
    workspace: "Workspace",
    copyPath: "Click to copy full path",
    loadFilesInfo: "Load files to view workspace content.",
    missingFileSave: "Missing — save to create.",
    panelToolAccess: "Tool Access",
    loadConfigTools: "Load config to adjust tool access.",
    usingExplicitAllowlist: "Using explicit allowlist.",
    usingFallbackTools: "Using fallback tool list.",
    toolProfile: "Profile",
    profileInherit: "Inherit",
    // Tool profile names
    profileMinimal: "Minimal",
    profileCoding: "Coding",
    profileMessaging: "Messaging",
    profileFull: "Full",
    // Tool section labels
    sectionFiles: "Files",
    sectionRuntime: "Runtime",
    sectionWeb: "Web",
    sectionMemory: "Memory",
    sectionSessions: "Sessions",
    sectionUi: "UI",
    sectionMessaging: "Messaging",
    sectionAutomation: "Automation",
    sectionNodes: "Nodes",
    sectionAgents: "Agents",
    sectionMedia: "Media",
    // Tool names
    toolRead: "Read",
    toolWrite: "Write",
    toolEdit: "Edit",
    toolApplyPatch: "Apply Patch",
    toolExec: "Execute",
    toolProcess: "Process",
    toolWebSearch: "Web Search",
    toolWebFetch: "Web Fetch",
    toolMemorySearch: "Memory Search",
    toolMemoryGet: "Memory Get",
    toolSessionsList: "List Sessions",
    toolSessionsHistory: "Session History",
    toolSessionsSend: "Session Send",
    toolSessionsSpawn: "Spawn Agent",
    toolSubagents: "Sub-agents",
    toolSessionStatus: "Session Status",
    toolBrowser: "Browser",
    toolCanvas: "Canvas",
    toolMessage: "Message",
    toolCron: "Cron",
    toolGateway: "Gateway",
    toolNodes: "Nodes",
    toolAgentsList: "Agents List",
    toolImage: "Image",
    toolTts: "Text-to-Speech",
    loadConfigSkills: "Load config to manage skills.",
    refreshToLoadSkills: "Refresh to load skill list.",
    refreshingToolCatalog: "Refreshing tool catalog…",
    loadChannelsInfo: "Load channels to see live status.",
    cronScheduler: "Scheduler",
    cronStatus: "Status",
    cronTotalJobs: "Total jobs",
    cronNextWake: "Next wake",
    cronAgentJobs: "Agent Jobs",
    cronNoJobs: "No jobs assigned.",
    cronOn: "on",
    cronOff: "off",
    orgRoot: "Root",
    // Agent Builder — 7-Step Character Simulation
    builderEdit: "Edit",
    builderTitle: "Configure Agent",
    builderS1Title: "Identity",
    builderS2Title: "Role",
    builderS3Title: "Style",
    builderS4Title: "Limits",
    builderS5Title: "Tools",
    builderS6Title: "Knowledge",
    builderS7Title: "Review",
    builderS1Question: "Who is this employee?",
    builderS2Question: "What is their job?",
    builderS3Question: "How do they communicate?",
    builderS4Question: "What are the red lines?",
    builderS5Question: "What tools do they use?",
    builderS6Question: "Teach them the job",
    builderS7Question: "Meet your new hire",
    builderName: "Name",
    builderNameHint: "e.g. Customer Support Lead",
    builderCreature: "Employee type",
    builderVibe: "Personality traits",
    builderFormality: "Formality level",
    formalityInformal: "Casual",
    formalityFormal: "Formal",
    formalityLevel_casual: "Casual and colloquial tone",
    formalityLevel_relaxed: "Relaxed and conversational tone",
    formalityLevel_balanced: "Balanced tone",
    formalityLevel_professional: "Professional and polished tone",
    formalityLevel_formal: "Formal and structured tone",
    builderPreviewLabel: "They would respond like this:",
    previewFormal: "Good day. How may I assist you?",
    previewInformal: "Hey! What can I help with?",
    previewBalanced: "Hello! How can I help you today?",
    builderPresets: "Quick presets",
    // Placement (org/team picker in Step 2)
    placement: "Affiliation",
    placementStandalone: "Independent",
    placementOrg: "Belongs to organization",
    placementSelectOrg: "Select organization…",
    placementSelectTeam: "Select team",
    placementYouAreHere: "You are here",
    placementAgentCount: "agent",
    placementNoOrgs: "No organizations yet",
    placementCreateOrgFirst: "Create an organization first",
    placementNoTeams: "No teams in this organization",
    builderChannels: "Channels",
    builderNoChannels: "No channels available.",
    builderNoChannelsConfigured:
      "No channels configured yet. Set up a channel below to get started.",
    builderChannelsNeedOrg: "Add this agent to an organization to assign channels.",
    builderChannelsAvailable: "Available channels",
    builderChannelBind: "Bind to this agent",
    builderChannelUnbind: "Unbind from this agent",
    builderChannelSetupHint: "Needs setup",
    builderChannelAccount: "Account",
    builderChannelFilter: "Filter",
    builderChannelFilterHint: "Specific number or group (optional)",
    builderSkills: "Skills",
    builderNoSkills: "No skills available.",
    builderSelectAll: "Select All",
    builderDeselectAll: "Deselect All",
    channelSysConnected: "System connected",
    channelSysConfigured: "System ready",
    channelNotConfigured: "Not configured",
    channelNotConnected: "Not connected",
    channelAvailable: "Available",
    // WhatsApp QR pairing
    builderChannelSetupBtn: "Set up",
    waQrStartBtn: "Set up with QR Code",
    waQrNoGateway: "Gateway connection required",
    waQrGenerating: "Generating QR code...",
    waQrScanPrompt: "Scan with WhatsApp",
    waQrStep1: "Open WhatsApp on your phone",
    waQrStep2: "Go to Settings > Linked Devices > Link a Device",
    waQrStep3: "Point your phone camera at the QR code",
    waQrWaiting: "Waiting for scan...",
    waQrSuccess: "WhatsApp connected successfully!",
    waQrDone: "Done",
    waQrTimeout: "QR code expired. Try again.",
    waQrRetry: "Try again",
    waQrCooldown: "Please wait {seconds}s before trying again to avoid account restrictions.",
    waQrErrorGeneric: "Failed to generate QR code. Check gateway connection.",
    waDisconnecting: "Disconnecting...",
    waDisconnect: "Disconnect device",
    waReconnect: "Reconnect",
    waRepair: "Re-pair device",
    // Generic channel controls
    chStart: "Connect",
    chStop: "Disconnect",
    builderAddAccount: "Add new account",
    builderChannelSetupGuide: "Configure this channel in Settings to get started.",
    builderCompleteness: "Completeness",
    // Skill labels (human-readable translations for tool IDs)
    skill_read_label: "Read Files",
    skill_read_desc: "Access and read file contents",
    skill_write_label: "Write Files",
    skill_write_desc: "Create and modify files",
    skill_edit_label: "Edit Files",
    skill_edit_desc: "Make targeted edits to existing files",
    skill_apply_patch_label: "Apply Patches",
    skill_apply_patch_desc: "Apply code patches and modifications",
    skill_exec_label: "Run Commands",
    skill_exec_desc: "Execute system commands and scripts",
    skill_process_label: "Manage Processes",
    skill_process_desc: "Control and monitor background processes",
    skill_web_search_label: "Web Search",
    skill_web_search_desc: "Search the internet for information",
    skill_web_fetch_label: "Fetch Web Pages",
    skill_web_fetch_desc: "Retrieve and read content from URLs",
    skill_memory_search_label: "Memory Search",
    skill_memory_search_desc: "Search through stored knowledge and documents",
    skill_image_generation_label: "Image Generation",
    skill_image_generation_desc: "Generate images from text descriptions",
    skill_code_interpreter_label: "Code Interpreter",
    skill_code_interpreter_desc: "Run and analyze code in real time",
    skill_email_label: "Email",
    skill_email_desc: "Send and manage email messages",
    skill_calendar_label: "Calendar",
    skill_calendar_desc: "Schedule and manage appointments",
    skill_send_message_label: "Send Messages",
    skill_send_message_desc: "Send messages to channels and contacts",
    skill_memory_get_label: "Read Memory",
    skill_memory_get_desc: "Read stored memory and knowledge files",
    skill_sessions_list_label: "List Sessions",
    skill_sessions_list_desc: "View all active chat sessions",
    skill_sessions_history_label: "Session History",
    skill_sessions_history_desc: "View past messages in a session",
    skill_sessions_send_label: "Send to Session",
    skill_sessions_send_desc: "Send a message to a specific session",
    skill_sessions_spawn_label: "Spawn Sub-agent",
    skill_sessions_spawn_desc: "Create a new sub-agent for a specific task",
    skill_subagents_label: "Manage Sub-agents",
    skill_subagents_desc: "Control and coordinate sub-agents",
    skill_session_status_label: "Session Status",
    skill_session_status_desc: "Check the status of a session",
    skill_browser_label: "Web Browser",
    skill_browser_desc: "Browse and interact with websites",
    skill_canvas_label: "Canvas Control",
    skill_canvas_desc: "Manage canvases and visual layouts",
    skill_message_label: "Send Messages",
    skill_message_desc: "Send messages to channels and contacts",
    skill_cron_label: "Schedule Tasks",
    skill_cron_desc: "Schedule recurring or delayed tasks",
    skill_gateway_label: "Gateway Control",
    skill_gateway_desc: "Manage gateway connections and settings",
    skill_nodes_label: "Nodes & Devices",
    skill_nodes_desc: "Manage connected nodes and devices",
    skill_agents_list_label: "List Agents",
    skill_agents_list_desc: "View all available agents",
    skill_image_label: "Image Understanding",
    skill_image_desc: "Analyze and understand images",
    skill_tts_label: "Text to Speech",
    skill_tts_desc: "Convert text into spoken audio",
    // WhatsApp QR gateway messages
    waQrAlreadyLinked:
      "WhatsApp is already connected. A new QR code is being generated to link a new device.",
    waQrDisconnected: "WhatsApp is disconnected. Try again.",
    // Creature chips
    creature_specialist: "Specialist",
    creature_manager: "Manager",
    creature_assistant: "Assistant",
    creature_advisor: "Advisor",
    // Vibe chips
    vibe_warm: "Warm",
    vibe_direct: "Direct",
    vibe_technical: "Technical",
    vibe_friendly: "Friendly",
    vibe_empathetic: "Empathetic",
    vibe_formal: "Formal",
    // Style chips
    style_warm: "Warm",
    style_direct: "Direct",
    style_technical: "Technical",
    style_friendly: "Friendly",
    style_empathetic: "Empathetic",
    // Constraint presets
    constraint_noPricing: "Never share internal pricing",
    constraint_noCompetitors: "Never discuss competitors",
    constraint_escalateComplaints: "Escalate complaints to manager",
    constraint_noPersonalData: "Never share personal data",
    // Persona fields (kept from old builder)
    builderPersonaRole: "Role & Purpose",
    builderPersonaRoleHint: "What is this agent responsible for?",
    builderPersonaRolePlaceholder: "You are a customer support specialist...",
    builderPersonaStyle: "Communication Style",
    builderPersonaStyleHint: "How should this agent communicate?",
    builderPersonaStylePlaceholder: "Professional, friendly, concise...",
    builderPersonaBehaviors: "Key Behaviors",
    builderPeersNetwork: "Communication Network",
    builderPeersHint:
      "Colleagues, managers, and departments this agent communicates with (auto-generated)",
    builderPersonaBehaviorsHint: "What specific actions should this agent take?",
    builderPersonaBehaviorsPlaceholder: "Always greet customers warmly...",
    builderPersonaConstraints: "Constraints",
    builderPersonaConstraintsHint: "What should this agent avoid doing?",
    builderPersonaConstraintsPlaceholder: "Never share internal pricing...",
    // builderShowRaw / builderShowStructured removed — no raw mode for end users
    builderKnowledgeComingSoon:
      "Knowledge upload will be available in the next update. You can skip this step for now.",
    builderNoPersona: "No persona configured",
    builderBack: "Back",
    builderNext: "Next",
    builderSave: "Save Agent",
    builderSaving: "Saving...",
    builderConfigure: "Configure",
    // Canvas overview
    defaultBadge: "Default",
    ovModel: "Model",
    ovWorkspace: "Workspace",
    ovSkills: "Skills",
    ovIdentity: "Identity",
    ovSkillsAll: "all",
    ovSkillsSelected: "{count} selected",
    ovModelConfig: "Model Configuration",
    ovPrimary: "Primary",
    ovPrimaryDefault: "Primary (default)",
    ovFallbacks: "Fallbacks",
    ovInheritDefault: "Inherit default",
    ovInherit: "Inherit ({model})",
    ovConfigNotLoaded: "Config not loaded yet.",
    ovReloadConfig: "Reload Config",
    ovLoading: "Loading…",
    ovDefaultChip: "★ Default",
  },
  canvas: {
    controls: "Canvas controls",
    undo: "Undo",
    redo: "Redo",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    fitView: "Fit view",
    emptyCanvas: "Empty canvas",
    dragToReposition: "Drag to reposition",
    clickToNavigate: "Click or drag to navigate",
  },
  config: {
    searchSettings: "Search settings…",
    resetToDefault: "Reset to default",
    removeItem: "Remove item",
    removeEntry: "Remove entry",
    jsonValue: "JSON value",
    key: "Key",
    unsupportedNode: "Unsupported schema node. Use Raw mode.",
    unsupportedType: "Unsupported type. Use Raw mode.",
    addEntry: "Add Entry",
    arrayEmpty: 'No items yet. Click "Add" to create one.',
    noCustomEntries: "No custom entries.",
  },
  debug: {
    snapshotsTitle: "Snapshots",
    snapshotsSubtitle: "Status, health, and heartbeat data.",
    status: "Status",
    securityAudit: "Security audit",
    health: "Health",
    lastHeartbeat: "Last heartbeat",
    rpcTitle: "Manual RPC",
    rpcSubtitle: "Send a raw gateway method with JSON params.",
    rpcMethod: "Method",
    rpcParams: "Params (JSON)",
    rpcCall: "Call",
    modelsTitle: "Models",
    modelsSubtitle: "Catalog from models.list.",
    eventsTitle: "Event Log",
    eventsSubtitle: "Latest gateway events.",
    eventsEmpty: "No events yet.",
  },
  devices: {
    rejectPairing: "Reject Pairing",
    rejectPairingConfirm: "Reject this device pairing request?",
    reject: "Reject",
    revokeToken: "Revoke Token",
    revokeTokenConfirm: "Revoke token for this device?",
    revoke: "Revoke",
  },
  gateway: {
    changeTitle: "Change Gateway URL",
    changeSubtitle: "This will reconnect to a different gateway server.",
    changeWarning: "Only confirm if you trust this URL. Malicious URLs can compromise your system.",
  },
  industries: {
    title: "Industries",
    search: "Search industries...",
    addIndustry: "Add Industry",
    builtIn: "Built-in",
    custom: "Custom",
    modified: "Modified",
    usedBy: "Used by {orgs} orgs ({agents} agents)",
    notUsed: "Not used yet",
    editTemplate: "Edit Template",
    save: "Save",
    saving: "Saving...",
    resetToDefault: "Reset to Default",
    pushToAgents: "Push to Agents",
    pushAll: "Update All Agents",
    pushingAll: "Updating all agents...",
    pushing: "Pushing...",
    deleteIndustry: "Delete Industry",
    confirmDelete: "Are you sure you want to delete this industry?",
    confirmDeleteUsed:
      "This industry is used by {orgs} organizations. Deleting it won't remove existing INDUSTRY.md files.",
    pushSuccess: "Updated {count} agents successfully",
    pushFailed: "Failed to update {count} agents",
    noChanges: "No changes to save",
    back: "Back",
    addFormTitle: "Add Custom Industry",
    addFormName: "Name",
    addFormNameHint: "e.g. Veterinary Medicine",
    addFormKey: "Key",
    addFormKeyHint: "Unique slug (e.g. veterinary)",
    addFormKeyAuto: "auto",
    addFormEmoji: "Emoji",
    addFormGroup: "Group",
    addFormNewGroup: "New group...",
    addFormGroupPlaceholder: "Enter group name...",
    addFormTemplate: "Template",
    addFormSave: "Save",
    addFormCancel: "Cancel",
    addFormKeyExists: "This key already exists",
    generateWithAI: "Generate with AI",
    generating: "Generating...",
    generateHint: "Enter a name first",
    templateSkeleton: "# {name} — Agent Operating Manual\n\n## BOUNDARIES\n...",
    generateSkeleton: "Generate Skeleton",
    structuredEditor: "Template Builder",
    rawEditor: "Raw Markdown",
    section_boundaries: "Boundaries",
    section_tone: "Tone & Style",
    section_intents: "Top Customer Intents",
    section_terms: "Key Terms",
    section_compliance: "Compliance",
    section_example: "Example Interaction",
    noResults: "No industries match your search.",
    editMeta: "Edit",
    aiRefineTitle: "AI-Assisted Editing",
    aiRefinePlaceholder:
      'Describe what you want to change... e.g. "Add a rule: never share customer data with third parties"',
    aiRefineApply: "Apply with AI",
    aiRefineApplying: "Applying...",
    aiRefineSuccess: "Template updated — review changes before saving.",
    aiRefineUndo: "Undo",
    aiRefineNoSession: "Open a chat session first to use AI editing.",
    aiRefineChanged: "Changed sections:",
    aiRefineNoChanges: "No structural changes detected.",
    aiRefineShowBefore: "Show previous version",
    aiRefineChangedBadge: "Modified by AI",
    section_title: "Title",
  },
  instances: {
    title: "Connected Instances",
    subtitle: "Presence beacons from the gateway and clients.",
    empty: "No instances reported yet.",
    scopesCount: "{count} scopes",
    lastInput: "Last input {time} ago",
    reason: "Reason",
  },
  logs: {
    searchLogs: "Search logs",
  },
  nodes: {
    title: "Nodes",
    subtitle: "Paired devices and live links.",
    empty: "No nodes found.",
    devicesTitle: "Devices",
    devicesSubtitle: "Pairing requests + role tokens.",
    pending: "Pending",
    paired: "Paired",
    devicesEmpty: "No paired devices.",
    approve: "Approve",
    reject: "Reject",
  },
  sessions: {
    subtitle: "Active session keys and per-session overrides.",
    activeWithin: "Active within (minutes)",
    limit: "Limit",
    includeGlobal: "Include global",
    includeUnknown: "Include unknown",
    colKey: "Key",
    colLabel: "Label",
    colKind: "Kind",
    colUpdated: "Updated",
    colTokens: "Tokens",
    colThinking: "Thinking",
    colVerbose: "Verbose",
    colReasoning: "Reasoning",
    colActions: "Actions",
    noSessions: "No sessions found.",
    deleteSession: "Delete Session",
    deleteSessionConfirm: "Deletes the session entry and archives its transcript.",
    mainSession: "Main Session",
    subagent: "Subagent:",
    cronJob: "Cron Job:",
    groupSuffix: "Group",
    sessionSuffix: "Session",
  },
  sidebar: {
    toolOutput: "Tool Output",
    close: "Close sidebar",
    viewRaw: "View Raw Text",
  },
  skills: {
    searchSkills: "Search skills",
  },
  theme: {
    theme: "Theme",
    system: "System",
    light: "Light",
    dark: "Dark",
  },
};
